import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useAddAdditionalEmailMutation } from 'src/composables/account/mutation/user/useAddAdditionalEmailMutation'
import { useRemoveAdditionalEmailMutation } from 'src/composables/account/mutation/user/useRemoveAdditionalEmailMutation'
import { useUpdateSettingsMutation } from 'src/composables/account/mutation/user/useUpdateSettingsMutation'
import { useVerifyAdditionalEmailMutation } from 'src/composables/account/mutation/user/useVerifyAdditionalEmailMutation'

import { useAccountStore } from 'src/stores/account'

import { THEME } from 'src/services/consts/app.const'
import { USER_FUNCTION_STATUS } from 'src/services/consts/user.const'
import { findPendingEmailVerification } from 'src/services/helpers/user.helper'

export const useAccountActionsStore = defineStore('account-actions', () => {
  const accountStore = useAccountStore()
  const { updateSettings, isUpdatingSettings } = useUpdateSettingsMutation()
  const { addAdditionalEmail: addAdditionalEmailMutation, isAddingAdditionalEmail } =
    useAddAdditionalEmailMutation()
  const { verifyAdditionalEmail: verifyAdditionalEmailMutation, isVerifyingAdditionalEmail } =
    useVerifyAdditionalEmailMutation()
  const { removeAdditionalEmail: removeAdditionalEmailMutation, isRemovingAdditionalEmail } =
    useRemoveAdditionalEmailMutation()

  async function toggleTheme() {
    const nextTheme = accountStore.theme === THEME.DARK ? THEME.LIGHT : THEME.DARK
    await updateSettings({ theme: nextTheme })
  }

  const isAddAdditionalEmailModalOpen = ref(false)
  const additionalEmailToVerify = ref(null)
  const isVerifyAdditionalEmailModalOpen = computed(() => additionalEmailToVerify.value !== null)

  // Done on the walkthrough's last card. A replay of a finished walkthrough has nothing to save.
  async function dismissOnboardingTour() {
    if (!accountStore.showOnboardingTour) return
    await updateSettings({ showOnboardingTour: false })
  }

  function openAddAdditionalEmailModal() {
    setEmailToVerify()
    isAddAdditionalEmailModalOpen.value = true
  }

  function closeAddAdditionalEmailModal() {
    isAddAdditionalEmailModalOpen.value = false
  }

  function setEmailToVerify(email = null) {
    additionalEmailToVerify.value = email || null
    if (additionalEmailToVerify.value) isAddAdditionalEmailModalOpen.value = false
  }

  // Which addresses have an add or remove in flight. Mutation flags are global,
  // so row spinners read this set instead.
  const activeEmails = ref(new Set())

  function setEmailLoading(email, isLoading) {
    if (!email) return
    const next = new Set(activeEmails.value)
    if (isLoading) next.add(email)
    else next.delete(email)
    activeEmails.value = next
  }

  function isEmailLoading(email) {
    return Boolean(email && activeEmails.value.has(email))
  }

  async function addAdditionalEmail({ email }) {
    setEmailLoading(email, true)
    try {
      const envelope = await addAdditionalEmailMutation({ email })
      if (envelope?.status === USER_FUNCTION_STATUS.VERIFICATION_SENT) {
        const pending = findPendingEmailVerification(envelope.userInfo, email)
        setEmailToVerify(pending?.email || email)
      }
      return envelope
    } finally {
      setEmailLoading(email, false)
    }
  }

  async function verifyAdditionalEmail({ email, code }) {
    const envelope = await verifyAdditionalEmailMutation({ email, code })
    if (envelope?.status === USER_FUNCTION_STATUS.SUCCESS) {
      setEmailToVerify()
    }
    return envelope
  }

  async function removeAdditionalEmail({ email, isPendingVerification = false }) {
    setEmailLoading(email, true)
    try {
      return await removeAdditionalEmailMutation({ email, isPendingVerification })
    } finally {
      setEmailLoading(email, false)
    }
  }

  const isMutatingUserInfo = computed(
    () =>
      isAddingAdditionalEmail.value ||
      isVerifyingAdditionalEmail.value ||
      isRemovingAdditionalEmail.value
  )

  return {
    toggleTheme,
    dismissOnboardingTour,
    isUpdatingSettings,
    addAdditionalEmail,
    isAddingAdditionalEmail,
    verifyAdditionalEmail,
    isVerifyingAdditionalEmail,
    removeAdditionalEmail,
    isRemovingAdditionalEmail,
    isEmailLoading,
    isMutatingUserInfo,
    isAddAdditionalEmailModalOpen,
    additionalEmailToVerify,
    isVerifyAdditionalEmailModalOpen,
    openAddAdditionalEmailModal,
    closeAddAdditionalEmailModal,
    setEmailToVerify,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAccountActionsStore, import.meta.hot))
}
